/**
 * Browser API client.
 *
 * The SPA talks to api-gateway directly. There is no BFF; auth lives in
 * the HttpOnly ``kos_session`` cookie that the gateway sets on
 * ``POST /v1/auth/google/callback`` (see ADR-0011). Every request goes
 * out with ``credentials: "include"`` so the browser attaches the cookie.
 *
 * - In dev, Vite proxies ``/api/v1`` to ``http://localhost:8090`` (see
 *   ``vite.config.ts``) so the cookie stays same-origin.
 * - In prod, set ``VITE_GATEWAY_URL`` to the gateway origin and the SPA
 *   will fetch it cross-origin (gateway must list the SPA origin in
 *   ``KOS_FRONTEND_ORIGINS`` and serve cookies with
 *   ``SameSite=None; Secure``).
 */

const PATH_PREFIX = "/api/v1";

function gatewayBase(): string {
  // ``import.meta.env`` is replaced statically by Vite at build time, so
  // this read costs nothing at runtime. Empty string => same-origin
  // (the dev proxy or a same-domain prod deploy).
  const url = import.meta.env.VITE_GATEWAY_URL;
  if (!url) return "";
  // Trim a trailing slash so callers can write ``apiUrl("/me")``.
  return url.replace(/\/$/, "");
}

export function apiUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = gatewayBase();
  // When ``VITE_GATEWAY_URL`` is set we skip the ``/api`` prefix because
  // that prefix only exists in the dev proxy / Next BFF. The gateway
  // itself serves ``/v1/*``. When ``VITE_GATEWAY_URL`` is empty we keep
  // ``/api`` so the dev proxy / a same-origin reverse proxy can route us.
  const prefix = base ? "/v1" : PATH_PREFIX;
  return `${base}${prefix}${path.startsWith("/") ? path : `/${path}`}`;
}

export function authHeaders(): Record<string, string> {
  // Auth is the cookie; nothing to inject. Kept for symmetry with the
  // upload helper and any callers that forward the result.
  return {};
}

function initJsonRequest(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers);
  for (const [key, value] of Object.entries(authHeaders())) {
    if (!headers.has(key)) headers.set(key, value);
  }
  const body = init?.body;
  if (body !== undefined && body !== null && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  // ``include`` is mandatory: without it the browser drops the
  // ``kos_session`` cookie on cross-origin requests, which silently
  // 401s every page that needs auth.
  return { credentials: "include", ...init, headers };
}

/**
 * Typed error for non-2xx responses from the gateway.
 *
 * Extends Error (so existing `err instanceof Error` checks keep working
 * and `.message` stays useful) and adds:
 *
 * - `status`: HTTP status code, for branching on 401 / 403 / 409 / etc.
 * - `code`: machine-readable code from the kos error envelope (e.g.
 *   `"conflict"`, `"validation_error"`), or `null` when the response
 *   body wasn't a kos envelope.
 * - `details`: free-form details bag from the envelope (e.g. the list
 *   of agent ids blocking an llm-config delete), or `null`.
 *
 * Backwards compatible: callers that just `toast.error(err.message)`
 * keep working unchanged.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: Record<string, unknown> | null;

  constructor(
    status: number,
    message: string,
    code: string | null,
    details: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function parseKosErrorBody(text: string): {
  code: string | null;
  message: string | null;
  details: Record<string, unknown> | null;
} {
  // Parsing is best-effort: gateway uses the kos envelope shape
  //   {"error": {"code": "...", "message": "...", "details": {...}}}
  // but a 502 / proxy hop might return non-JSON; degrade to nulls in
  // that case so the caller sees the raw text in `Error.message`.
  if (!text) return { code: null, message: null, details: null };
  try {
    const parsed = JSON.parse(text) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      "error" in (parsed as Record<string, unknown>) &&
      typeof (parsed as { error?: unknown }).error === "object" &&
      (parsed as { error: unknown }).error !== null
    ) {
      const env = (parsed as { error: Record<string, unknown> }).error;
      return {
        code: typeof env.code === "string" ? env.code : null,
        message: typeof env.message === "string" ? env.message : null,
        details:
          env.details && typeof env.details === "object"
            ? (env.details as Record<string, unknown>)
            : null,
      };
    }
  } catch {
    // not JSON; fall through to nulls
  }
  return { code: null, message: null, details: null };
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(path), initJsonRequest(init));
  if (!res.ok) {
    const text = await res.text();
    const env = parseKosErrorBody(text);
    throw new ApiError(res.status, env.message ?? `${res.status} ${text}`, env.code, env.details);
  }
  const txt = await res.text();
  if (!txt) return undefined as T;
  return JSON.parse(txt) as T;
}

export async function apiRaw(path: string, init?: RequestInit): Promise<Response> {
  return fetch(apiUrl(path), initJsonRequest(init));
}

export async function apiUpload<T>(
  path: string,
  files: File[],
  form: Record<string, string | undefined> = {},
): Promise<T> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(form)) {
    if (typeof v === "string" && v.length > 0) fd.append(k, v);
  }
  for (const f of files) fd.append("file", f, f.name);
  const res = await fetch(apiUrl(path), {
    method: "POST",
    body: fd,
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  const txt = await res.text();
  if (!txt) return undefined as T;
  return JSON.parse(txt) as T;
}
